/*
 * <projects-overview> custom element.
 *
 * Owns all client-side state for the Projects Overview section:
 * filter (AND across categories, OR within), sort, view toggle, and
 * load-more pagination. All cards are rendered in the DOM on initial
 * page load; this element only shows/hides and reorders them.
 */

const URL_PREFIX = 'projects';
const FILTER_KEYS = ['space', 'product_type', 'collection', 'location'];
const FILTER_KEY_TO_STATE = {
  space: 'space',
  product_type: 'productType',
  collection: 'collection',
  location: 'location',
};
const CARD_ATTR_FOR_KEY = {
  space: 'space',
  productType: 'productTypes',
  collection: 'collectionHandles',
  location: 'location',
};
// Filters whose card attribute serializes a list of values joined with
// `##||##` (product type values can themselves contain `|`; projects can
// belong to multiple collections).
const MULTI_VALUE_STATE_KEYS = new Set(['productType', 'collection']);
const MULTI_VALUE_DELIM = '##||##';
// Must match the CSS transition duration on `.projects-overview__facet-dropdown`.
const FACET_TRANSITION_MS = 450;

class ProjectsOverview extends HTMLElement {
  constructor() {
    super();
    this._onPopState = this._onPopState.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onDrawerKeydown = this._onDrawerKeydown.bind(this);
    this._onShopifySectionSelect = this._onShopifySectionSelect.bind(this);
    this._onShopifySectionDeselect = this._onShopifySectionDeselect.bind(this);
  }

  connectedCallback() {
    this.grid = this.querySelector('[data-projects-grid]');
    if (!this.grid) return;

    this.cards = Array.from(this.querySelectorAll('[data-project-card]'));
    this.sortSelect = this.querySelector('[data-projects-sort]');
    this.loadMoreBtn = this.querySelector('[data-projects-load-more]');
    this.visibleCountEl = this.querySelector('[data-projects-visible]');
    this.totalCountEl = this.querySelector('[data-projects-total]');
    this.drawer = this.querySelector('[data-projects-drawer]');
    this.viewToggle = this.querySelector('[data-projects-view-toggle]');
    this.openDrawerBtn = this.querySelector('[data-projects-open-drawer]');
    this.activeFiltersCountEl = this.querySelector('[data-active-filters-count]');
    this.drawerShowCountEl = this.querySelector('[data-drawer-show-count]');
    this.drawerClearCountEl = this.querySelector('[data-drawer-clear-count]');

    this.initialCount = parseInt(this.dataset.initialCount, 10) || 10;

    this.state = {
      filters: {
        space: new Set(),
        productType: new Set(),
        collection: new Set(),
        location: new Set(),
      },
      sort: this.dataset.defaultSort || 'default',
      view: this.dataset.defaultViewDesktop || '2',
      mobileView: this.dataset.defaultViewMobile || '1',
      visibleCount: this.initialCount,
    };

    this._parseURL();
    this._bindListeners();
    this._syncCheckboxesFromState();
    if (this.sortSelect) this.sortSelect.value = this.state.sort;
    this._applyAll();
    // Dropdown items are reachable via Tab only after their toggle is activated,
    // so the dropdowns don't sit in the natural tab order alongside the toggles.
    this.querySelectorAll('.projects-overview__facet-dropdown')
      .forEach((d) => this._setDropdownTabReachable(d, false));
    this._syncURL({ replace: true });
    window.addEventListener('popstate', this._onPopState);

    // Shopify theme editor — close any open drawer/dropdowns when the merchant
    // selects/deselects this section so the editor reflects a stable state.
    // Section reorders/setting changes re-render the section, which triggers
    // disconnectedCallback + connectedCallback naturally.
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', this._onShopifySectionSelect);
      document.addEventListener('shopify:section:deselect', this._onShopifySectionDeselect);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this._onPopState);
    document.removeEventListener('click', this._onDocumentClick);
    document.removeEventListener('shopify:section:select', this._onShopifySectionSelect);
    document.removeEventListener('shopify:section:deselect', this._onShopifySectionDeselect);
    // Restore body scroll in case the section is unloaded while the drawer is open
    if (this.drawer && this.drawer.classList.contains('is-open')) {
      document.body.style.overflow = '';
    }
  }

  _onShopifySectionSelect(event) {
    if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
    this._closeDrawer();
    this._closeAllFacets();
  }

  _onShopifySectionDeselect(event) {
    if (!event.detail || event.detail.sectionId !== this.dataset.sectionId) return;
    this._closeDrawer();
    this._closeAllFacets();
  }

  /* ---------- URL state ---------- */

  _parseURL() {
    const params = new URLSearchParams(window.location.search);
    FILTER_KEYS.forEach((key) => {
      const raw = params.get(`${URL_PREFIX}.${key}`);
      if (!raw) return;
      const stateKey = FILTER_KEY_TO_STATE[key];
      raw.split(',').filter(Boolean).forEach((v) => this.state.filters[stateKey].add(v));
    });
    const sort = params.get(`${URL_PREFIX}.sort`);
    if (sort) this.state.sort = sort;
    const view = params.get(`${URL_PREFIX}.view`);
    if (view) this.state.view = view;
    const mobileView = params.get(`${URL_PREFIX}.view_mobile`);
    if (mobileView) this.state.mobileView = mobileView;
  }

  _syncURL({ replace = false } = {}) {
    const url = new URL(window.location.href);
    Array.from(url.searchParams.keys())
      .filter((k) => k.startsWith(`${URL_PREFIX}.`))
      .forEach((k) => url.searchParams.delete(k));

    FILTER_KEYS.forEach((key) => {
      const stateKey = FILTER_KEY_TO_STATE[key];
      const vals = Array.from(this.state.filters[stateKey]);
      if (vals.length) url.searchParams.set(`${URL_PREFIX}.${key}`, vals.join(','));
    });

    if (this.state.sort !== 'default') {
      url.searchParams.set(`${URL_PREFIX}.sort`, this.state.sort);
    }
    if (this.state.view !== (this.dataset.defaultViewDesktop || '2')) {
      url.searchParams.set(`${URL_PREFIX}.view`, this.state.view);
    }
    if (this.state.mobileView !== (this.dataset.defaultViewMobile || '1')) {
      url.searchParams.set(`${URL_PREFIX}.view_mobile`, this.state.mobileView);
    }

    const method = replace ? 'replaceState' : 'pushState';
    window.history[method]({}, '', url.toString());
  }

  _onPopState() {
    FILTER_KEYS.forEach((key) => {
      const stateKey = FILTER_KEY_TO_STATE[key];
      this.state.filters[stateKey].clear();
    });
    this.state.sort = this.dataset.defaultSort || 'default';
    this.state.view = this.dataset.defaultViewDesktop || '2';
    this.state.mobileView = this.dataset.defaultViewMobile || '1';
    this.state.visibleCount = this.initialCount;
    this._parseURL();
    this._syncCheckboxesFromState();
    if (this.sortSelect) this.sortSelect.value = this.state.sort;
    this._applyAll();
  }

  /* ---------- Listeners ---------- */

  _bindListeners() {
    this.addEventListener('change', (e) => {
      const target = e.target;
      if (target.matches('input[type="checkbox"][data-pt-category-toggle]')) {
        this._onCategoryToggleChange(target);
      } else if (target.matches('input[type="checkbox"][data-filter-key]')) {
        this._onFilterChange(target);
      } else if (this.sortSelect && target === this.sortSelect) {
        this.state.sort = target.value;
        this._applySort();
        this._applyPagination();
        this._updateCountText();
        this._syncURL();
      }
    });

    if (this.openDrawerBtn) {
      this.openDrawerBtn.addEventListener('click', () => {
        this._openDrawer();
      });
    }

    this.addEventListener('click', (e) => {
      const facetToggle = e.target.closest('.projects-overview__facet-toggle');
      if (facetToggle) {
        const facet = facetToggle.closest('.projects-overview__facet');
        if (facet) {
          this._handleFacetToggleClick(facet.dataset.facetKey, facet);
        }
        return;
      }

      const drawerOpen = e.target.closest('[data-projects-open-drawer]');
      if (drawerOpen) {
        this._openDrawer();
        return;
      }

      const drawerClose = e.target.closest('[data-projects-close-drawer]');
      if (drawerClose) {
        this._closeDrawer();
        return;
      }

      const clearAll = e.target.closest('[data-projects-clear-all]');
      if (clearAll) {
        this._clearAllFilters();
        return;
      }

      const ptCollapse = e.target.closest('[data-pt-collapse]');
      if (ptCollapse) {
        const column = ptCollapse.closest('[data-pt-category]');
        if (column) {
          const collapsed = column.classList.toggle('is-collapsed');
          ptCollapse.setAttribute('aria-expanded', (!collapsed).toString());
          const panel = column.querySelector('[data-pt-leaves]');
          if (panel) panel.hidden = collapsed;
        }
        return;
      }

      const filterToggle = e.target.closest('.drawer-filter--projects .filter-toggle');
      if (filterToggle) {
        const expanded = filterToggle.getAttribute('aria-expanded') === 'true';
        filterToggle.setAttribute('aria-expanded', (!expanded).toString());
        const content = filterToggle.nextElementSibling;
        if (content) content.hidden = expanded;
        return;
      }

      const loadMore = e.target.closest('[data-projects-load-more]');
      if (loadMore) {
        this.state.visibleCount += this.initialCount;
        this._applyPagination();
        this._updateCountText();
        return;
      }

      const viewBtn = e.target.closest('.projects-overview__view-btn');
      if (viewBtn) {
        if (viewBtn.dataset.viewDesktop) {
          this.state.view = viewBtn.dataset.viewDesktop;
        } else if (viewBtn.dataset.viewMobile) {
          this.state.mobileView = viewBtn.dataset.viewMobile;
        }
        this._applyView();
        this._syncURL();
        return;
      }
    });

    // When tabbing past the last focusable in an open dropdown, return focus
    // to the toggle that opened it so keyboard users land back on the facets row.
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || e.shiftKey) return;
      const dropdown = e.target.closest('.projects-overview__facet-dropdown.is-open');
      if (!dropdown) return;
      const focusables = this._dropdownFocusables(dropdown);
      if (focusables.length === 0) return;
      if (e.target === focusables[focusables.length - 1] && this._activeFacetToggle) {
        e.preventDefault();
        this._activeFacetToggle.focus();
        // Once focus has left this dropdown, take it out of the tab order
        // so subsequent Tab navigation can't loop back into it.
        this._setDropdownTabReachable(dropdown, false);
      }
    });

    // Close open facet dropdowns when clicking outside.
    // Bound once in constructor so disconnectedCallback can remove it on teardown.
    document.addEventListener('click', this._onDocumentClick);
  }

  _onDocumentClick(e) {
    if (!this.contains(e.target)) {
      this._closeAllFacets();
    }
  }

  _handleFacetToggleClick(key, facet) {
    // A new click during an in-flight open supersedes that pending open.
    if (this._pendingOpenTimer) {
      clearTimeout(this._pendingOpenTimer);
      this._pendingOpenTimer = null;
      const wrapper = this.querySelector('[data-projects-facet-dropdowns]');
      if (wrapper) wrapper.classList.remove('has-anim-open');
    }

    const currentOpenFacet = this.querySelector('.projects-overview__facet.is-open');

    if (currentOpenFacet === facet) {
      this._closeAllFacets();
      return;
    }

    if (currentOpenFacet) {
      // Close current, then open new after the close transition completes
      // so the user sees A fade out before B fades in. Hold the wrapper's
      // "open" visual state across the gap so padding/border don't flicker.
      this._closeAllFacets();
      const wrapper = this.querySelector('[data-projects-facet-dropdowns]');
      if (wrapper) wrapper.classList.add('has-anim-open');
      this._pendingOpenTimer = setTimeout(() => {
        this._pendingOpenTimer = null;
        if (wrapper) wrapper.classList.remove('has-anim-open');
        this._openFacet(key);
      }, FACET_TRANSITION_MS);
      return;
    }

    this._openFacet(key);
  }

  _openFacet(key) {
    if (!key) return;
    const facet = this.querySelector(`.projects-overview__facet[data-facet-key="${CSS.escape(key)}"]`);
    const dropdown = this.querySelector(`.projects-overview__facet-dropdown[data-facet-key="${CSS.escape(key)}"]`);
    if (facet) facet.classList.add('is-open');
    if (dropdown) dropdown.classList.add('is-open');
    const toggle = facet && facet.querySelector('.projects-overview__facet-toggle');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'true');
      this._activeFacetToggle = toggle;
    }
    // Move focus into the dropdown so keyboard users continue tabbing through it
    if (dropdown) {
      this._setDropdownTabReachable(dropdown, true);
      const focusable = this._dropdownFocusables(dropdown)[0];
      if (focusable) focusable.focus();
    }
  }

  _dropdownFocusables(dropdown) {
    return Array.from(
      dropdown.querySelectorAll('input:not([disabled]):not([type="hidden"]), button:not([disabled])')
    ).filter((el) => el.offsetParent !== null && el.tabIndex !== -1);
  }

  _setDropdownTabReachable(dropdown, reachable) {
    if (!dropdown) return;
    dropdown.querySelectorAll('input, button').forEach((el) => {
      if (reachable) {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', '-1');
      }
    });
  }

  _closeAllFacets() {
    if (this._pendingOpenTimer) {
      clearTimeout(this._pendingOpenTimer);
      this._pendingOpenTimer = null;
    }
    const wrapper = this.querySelector('[data-projects-facet-dropdowns]');
    if (wrapper) wrapper.classList.remove('has-anim-open');
    this.querySelectorAll('.projects-overview__facet.is-open, .projects-overview__facet-dropdown.is-open')
      .forEach((el) => el.classList.remove('is-open'));
    this.querySelectorAll('.projects-overview__facet-toggle[aria-expanded="true"]')
      .forEach((t) => t.setAttribute('aria-expanded', 'false'));
    this.querySelectorAll('.projects-overview__facet-dropdown')
      .forEach((d) => this._setDropdownTabReachable(d, false));
    this._activeFacetToggle = null;
  }

  _onFilterChange(input) {
    const key = input.dataset.filterKey;
    const value = input.dataset.filterValue;
    const stateKey = FILTER_KEY_TO_STATE[key];
    if (!stateKey) return;

    if (input.checked) {
      this.state.filters[stateKey].add(value);
    } else {
      this.state.filters[stateKey].delete(value);
    }

    // Mirror to matching inputs (keeps desktop + drawer in sync)
    this._mirrorCheckbox(key, value, input.checked, input);

    if (key === 'product_type') {
      this._syncCategoryToggles(input.dataset.ptCategory);
    }

    this._applyFilters();
    this._applySort();
    this._applyPagination();
    this._updateFacetCounts();
    this._updateActiveFilterCounts();
    this._updateCountText();
    this._syncURL();
  }

  _onCategoryToggleChange(toggleInput) {
    const category = toggleInput.dataset.ptCategoryToggle;
    if (!category) return;
    const checked = toggleInput.checked;

    // Mirror category toggle across desktop + drawer
    this.querySelectorAll(
      `input[type="checkbox"][data-pt-category-toggle="${CSS.escape(category)}"]`
    ).forEach((el) => {
      if (el !== toggleInput) el.checked = checked;
    });

    // Apply to all child leaves (filter inputs sharing this category)
    const leaves = this.querySelectorAll(
      `input[type="checkbox"][data-filter-key="product_type"][data-pt-category="${CSS.escape(category)}"]`
    );
    const set = this.state.filters.productType;
    const seenValues = new Set();
    leaves.forEach((leaf) => {
      leaf.checked = checked;
      const value = leaf.dataset.filterValue;
      if (seenValues.has(value)) return;
      seenValues.add(value);
      if (checked) set.add(value);
      else set.delete(value);
    });

    this._applyCategoryDimming();
    this._applyFilters();
    this._applySort();
    this._applyPagination();
    this._updateFacetCounts();
    this._updateActiveFilterCounts();
    this._updateCountText();
    this._syncURL();
  }

  _syncCategoryToggles(category) {
    if (!category) return;
    const leaves = this.querySelectorAll(
      `input[type="checkbox"][data-filter-key="product_type"][data-pt-category="${CSS.escape(category)}"]`
    );
    // Use distinct filter values — desktop + drawer mirror duplicates
    const values = new Set();
    leaves.forEach((l) => values.add(l.dataset.filterValue));
    const allChecked = Array.from(values).every((v) => this.state.filters.productType.has(v));

    this.querySelectorAll(
      `input[type="checkbox"][data-pt-category-toggle="${CSS.escape(category)}"]`
    ).forEach((toggle) => {
      toggle.checked = allChecked;
    });

    this._applyCategoryDimming();
  }

  _applyCategoryDimming() {
    const categories = new Set();
    this.querySelectorAll('input[type="checkbox"][data-pt-category-toggle]').forEach((t) => {
      categories.add(t.dataset.ptCategoryToggle);
    });
    categories.forEach((category) => {
      const toggle = this.querySelector(
        `input[type="checkbox"][data-pt-category-toggle="${CSS.escape(category)}"]`
      );
      const checked = toggle ? toggle.checked : false;
      this.querySelectorAll(`[data-pt-category="${CSS.escape(category)}"]:not(input)`).forEach((el) => {
        el.classList.toggle('is-category-checked', checked);
      });
    });
  }

  _mirrorCheckbox(key, value, checked, source) {
    const matches = this.querySelectorAll(
      `input[type="checkbox"][data-filter-key="${key}"][data-filter-value="${CSS.escape(value)}"]`
    );
    matches.forEach((el) => {
      if (el !== source) el.checked = checked;
    });
  }

  _syncCheckboxesFromState() {
    this.querySelectorAll('input[type="checkbox"][data-filter-key]').forEach((input) => {
      const stateKey = FILTER_KEY_TO_STATE[input.dataset.filterKey];
      if (!stateKey) return;
      input.checked = this.state.filters[stateKey].has(input.dataset.filterValue);
    });
    // Derive each category toggle's checked state from its leaves
    const categories = new Set();
    this.querySelectorAll('input[type="checkbox"][data-pt-category-toggle]').forEach((t) => {
      categories.add(t.dataset.ptCategoryToggle);
    });
    categories.forEach((category) => this._syncCategoryToggles(category));
  }

  _clearAllFilters() {
    FILTER_KEYS.forEach((key) => {
      this.state.filters[FILTER_KEY_TO_STATE[key]].clear();
    });
    this._syncCheckboxesFromState();
    this._applyFilters();
    this._applySort();
    this._applyPagination();
    this._updateFacetCounts();
    this._updateActiveFilterCounts();
    this._updateCountText();
    this._syncURL();
  }

  /* ---------- Apply state ---------- */

  _applyAll() {
    this._applyFilters();
    this._applySort();
    this._applyPagination();
    this._applyView();
    this._updateFacetCounts();
    this._updateActiveFilterCounts();
    this._updateCountText();
  }

  _applyFilters() {
    this.cards.forEach((card) => {
      const matches = this._cardMatchesFilters(card, this.state.filters);
      if (matches) {
        card.removeAttribute('data-hidden-by-filter');
      } else {
        card.setAttribute('data-hidden-by-filter', '');
      }
    });
    // reset pagination on filter change
    this.state.visibleCount = this.initialCount;
  }

  _cardMatchesFilters(card, filters) {
    for (const stateKey of Object.keys(filters)) {
      const set = filters[stateKey];
      if (set.size === 0) continue;
      const cardValue = card.dataset[CARD_ATTR_FOR_KEY[stateKey]] || '';
      if (MULTI_VALUE_STATE_KEYS.has(stateKey)) {
        const values = cardValue.split(MULTI_VALUE_DELIM).filter(Boolean);
        if (!values.some((v) => set.has(v))) return false;
      } else {
        if (!set.has(cardValue)) return false;
      }
    }
    return true;
  }

  _applySort() {
    const sort = this.state.sort;
    const sorted = this.cards.slice();
    if (sort === 'title-asc') {
      sorted.sort((a, b) => a.dataset.titleSort.localeCompare(b.dataset.titleSort));
    } else if (sort === 'title-desc') {
      sorted.sort((a, b) => b.dataset.titleSort.localeCompare(a.dataset.titleSort));
    } else {
      sorted.sort(
        (a, b) => parseInt(a.dataset.index, 10) - parseInt(b.dataset.index, 10)
      );
    }
    const frag = document.createDocumentFragment();
    sorted.forEach((c) => frag.appendChild(c));
    this.grid.appendChild(frag);
  }

  _applyPagination() {
    // Work in current DOM order so restriping + pagination both follow sort order
    const orderedVisible = this._currentOrderedCards().filter(
      (c) => !c.hasAttribute('data-hidden-by-filter')
    );

    // Re-stripe narrow/wide pattern across currently-visible (post-filter, post-sort)
    // cards so the 2-col alternating grid stays balanced when rows shift.
    this._restripeLayouts(orderedVisible);

    orderedVisible.forEach((card, i) => {
      if (i < this.state.visibleCount) {
        card.removeAttribute('data-hidden-by-pagination');
      } else {
        card.setAttribute('data-hidden-by-pagination', '');
      }
    });

    if (this.loadMoreBtn) {
      const total = orderedVisible.length;
      this.loadMoreBtn.hidden = total <= this.state.visibleCount;
    }
  }

  _currentOrderedCards() {
    return Array.from(this.grid.querySelectorAll('[data-project-card]'));
  }

  _restripeLayouts(visibleCards) {
    // Pattern per spec (mod 4): 0 narrow, 1 wide, 2 wide, 3 narrow
    visibleCards.forEach((card, i) => {
      const mod = i % 4;
      const layout = mod === 0 || mod === 3 ? 'narrow' : 'wide';
      card.classList.toggle('projects-overview__card--narrow', layout === 'narrow');
      card.classList.toggle('projects-overview__card--wide', layout === 'wide');
      card.dataset.layout = layout;
    });
  }

  _applyView() {
    this.grid.dataset.view = `${this.state.view}col`;
    this.grid.dataset.viewMobile = `${this.state.mobileView}col`;

    if (this.viewToggle) {
      this.viewToggle
        .querySelectorAll('[data-view-desktop]')
        .forEach((btn) => btn.classList.toggle('is-active', btn.dataset.viewDesktop === this.state.view));
      this.viewToggle
        .querySelectorAll('[data-view-mobile]')
        .forEach((btn) => btn.classList.toggle('is-active', btn.dataset.viewMobile === this.state.mobileView));
    }
  }

  /* ---------- Facet counts ---------- */

  _updateFacetCounts() {
    FILTER_KEYS.forEach((key) => {
      const stateKey = FILTER_KEY_TO_STATE[key];
      // Build filter set that excludes this category
      const filtersExcludingSelf = { ...this.state.filters };
      filtersExcludingSelf[stateKey] = new Set();

      const candidateCards = this.cards.filter((c) => this._cardMatchesFilters(c, filtersExcludingSelf));

      this.querySelectorAll(`input[type="checkbox"][data-filter-key="${key}"]`).forEach((input) => {
        const value = input.dataset.filterValue;
        const attr = CARD_ATTR_FOR_KEY[stateKey];
        let count = 0;
        if (MULTI_VALUE_STATE_KEYS.has(stateKey)) {
          count = candidateCards.filter((c) => {
            const values = (c.dataset[attr] || '').split(MULTI_VALUE_DELIM).filter(Boolean);
            return values.includes(value);
          }).length;
        } else {
          count = candidateCards.filter((c) => (c.dataset[attr] || '') === value).length;
        }

        const label = input.closest('label');
        if (label) {
          const countEls = label.querySelectorAll('[data-option-count]');
          countEls.forEach((el) => {
            el.textContent = `(${count})`;
          });
        }
        // Disable zero-count options unless already active
        input.disabled = count === 0 && !input.checked;
      });
    });

    this._updateCategoryCountsAndStates();
  }

  _updateCategoryCountsAndStates() {
    // Category headers show the SUM of their leaves' counts.
    const categories = new Set();
    this.querySelectorAll('input[type="checkbox"][data-pt-category-toggle]').forEach((t) => {
      categories.add(t.dataset.ptCategoryToggle);
    });

    categories.forEach((category) => {
      const escaped = CSS.escape(category);
      // Pull leaf counts from any one rendering (desktop OR drawer); they're
      // computed identically. Use distinct filter values to avoid double-counting
      // when desktop+drawer both render the same leaf.
      const seen = new Set();
      let total = 0;
      let allDisabled = true;
      this.querySelectorAll(
        `input[type="checkbox"][data-filter-key="product_type"][data-pt-category="${escaped}"]`
      ).forEach((leafInput) => {
        const v = leafInput.dataset.filterValue;
        if (seen.has(v)) return;
        seen.add(v);
        const label = leafInput.closest('label');
        const countEl = label && label.querySelector('[data-option-count]');
        const text = countEl ? countEl.textContent : '';
        const m = text.match(/\((\d+)\)/);
        const n = m ? parseInt(m[1], 10) : 0;
        total += n;
        if (!leafInput.disabled) allDisabled = false;
      });

      this.querySelectorAll(
        `input[type="checkbox"][data-pt-category-toggle="${escaped}"]`
      ).forEach((toggle) => {
        toggle.disabled = allDisabled && !toggle.checked;
        const label = toggle.closest('label');
        if (!label) return;
        label.querySelectorAll('[data-pt-category-count]').forEach((el) => {
          el.textContent = `(${total})`;
        });
      });
    });
  }

  _updateActiveFilterCounts() {
    let total = 0;
    FILTER_KEYS.forEach((key) => {
      const stateKey = FILTER_KEY_TO_STATE[key];
      const size = this.state.filters[stateKey].size;
      total += size;

      // Update per-facet active count
      this.querySelectorAll(`.projects-overview__facet[data-facet-key="${key}"] [data-facet-active-count]`)
        .forEach((el) => {
          el.textContent = size > 0 ? `(${size})` : '';
        });
      this.querySelectorAll(`[data-drawer-facet-key="${key}"] [data-drawer-facet-count]`)
        .forEach((el) => {
          el.textContent = size > 0 ? `(${size})` : '';
        });
    });

    if (this.activeFiltersCountEl) {
      this.activeFiltersCountEl.textContent = total > 0 ? `(${total})` : '';
    }
    if (this.drawerClearCountEl) {
      this.drawerClearCountEl.textContent = total > 0 ? `(${total})` : '';
    }
  }

  _updateCountText() {
    const visibleFiltered = this.cards.filter((c) => !c.hasAttribute('data-hidden-by-filter'));
    const shown = Math.min(this.state.visibleCount, visibleFiltered.length);
    if (this.visibleCountEl) this.visibleCountEl.textContent = shown;
    if (this.totalCountEl) this.totalCountEl.textContent = visibleFiltered.length;
    if (this.drawerShowCountEl) {
      this.drawerShowCountEl.textContent = visibleFiltered.length > 0 ? `(${visibleFiltered.length})` : '';
    }
  }

  /* ---------- Drawer ---------- */

  _openDrawer() {
    if (!this.drawer) return;
    // Remember what to send focus back to on close (the trigger that opened it,
    // or whatever currently has focus as a fallback).
    const active = document.activeElement;
    this._drawerOpener = active instanceof HTMLElement ? active : this.openDrawerBtn;
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.drawer.addEventListener('keydown', this._onDrawerKeydown);
    const focusable = this._drawerFocusables()[0];
    if (focusable) focusable.focus();
  }

  _closeDrawer() {
    if (!this.drawer) return;
    const wasOpen = this.drawer.classList.contains('is-open');
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    this.drawer.removeEventListener('keydown', this._onDrawerKeydown);
    if (wasOpen && this._drawerOpener && typeof this._drawerOpener.focus === 'function') {
      this._drawerOpener.focus();
    }
    this._drawerOpener = null;
  }

  _drawerFocusables() {
    if (!this.drawer) return [];
    return Array.from(
      this.drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => el.offsetParent !== null);
  }

  _onDrawerKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault();
      this._closeDrawer();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = this._drawerFocusables();
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('projects-overview')) {
  customElements.define('projects-overview', ProjectsOverview);
}
